import { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";

/**
 * Dual-currency context for the marketing site.
 *
 * - Auto-detects currency via timezone (fast, no network) with optional
 *   IP-based geo-refinement (soft fallback).
 * - South Africa → ZAR, everywhere else → USD.
 * - Manual override via setCurrency() persists to localStorage and wins over
 *   geo-detection.
 * - FX rate (R per $1) is fetched from /api/settings/fx-rate on mount so it
 *   can be updated without a code deploy. Falls back to 18 if the API is
 *   unreachable.
 * - Paystack always charges in ZAR — USD is display-only.
 */

const STORAGE_KEY = "pg_currency_v1";
const DEFAULT_FX_RATE = 18;
const API = process.env.REACT_APP_BACKEND_URL;

const CurrencyContext = createContext(null);

function detectCurrencyByTimezone() {
    try {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
        if (tz.includes("Africa/Johannesburg")) return "ZAR";
    } catch (e) {
        // SSR / non-browser — ignore
    }
    return "USD";
}

async function detectCurrencyByIp() {
    try {
        const res = await fetch("https://ipapi.co/country/", { cache: "force-cache" });
        if (!res.ok) return null;
        const cc = (await res.text()).trim().toUpperCase();
        return cc === "ZA" ? "ZAR" : "USD";
    } catch (e) {
        return null;
    }
}

export function CurrencyProvider({ children }) {
    const [currency, setCurrencyState] = useState(() => {
        if (typeof window === "undefined") return "ZAR";
        try {
            const saved = localStorage.getItem(STORAGE_KEY);
            if (saved === "ZAR" || saved === "USD") return saved;
        } catch (e) { /* noop */ }
        return detectCurrencyByTimezone();
    });
    const [manual, setManual] = useState(() => {
        if (typeof window === "undefined") return false;
        try { return !!localStorage.getItem(STORAGE_KEY); } catch (e) { return false; }
    });
    const [fxRate, setFxRate] = useState(DEFAULT_FX_RATE);

    // Soft upgrade currency via IP lookup when no manual preference has been set.
    useEffect(() => {
        if (manual) return;
        let cancelled = false;
        (async () => {
            const ipCur = await detectCurrencyByIp();
            if (!cancelled && ipCur && ipCur !== currency) setCurrencyState(ipCur);
        })();
        return () => { cancelled = true; };
    }, [manual, currency]);

    // Fetch live FX rate from backend on mount. Fallback silently if API is down.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await axios.get(`${API}/api/settings/fx-rate`, { timeout: 5000 });
                const rate = Number(res?.data?.rate);
                if (!cancelled && Number.isFinite(rate) && rate > 0) {
                    setFxRate(rate);
                }
            } catch (e) {
                // Keep DEFAULT_FX_RATE — fail quietly, do not block render.
            }
        })();
        return () => { cancelled = true; };
    }, []);

    const setCurrency = useCallback((code) => {
        if (code !== "ZAR" && code !== "USD") return;
        setCurrencyState(code);
        setManual(true);
        try { localStorage.setItem(STORAGE_KEY, code); } catch (e) { /* noop */ }
    }, []);

    const value = { currency, setCurrency, fxRate };
    return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
}

export function useCurrency() {
    const ctx = useContext(CurrencyContext);
    if (!ctx) {
        return { currency: "ZAR", setCurrency: () => {}, fxRate: DEFAULT_FX_RATE };
    }
    return ctx;
}

/** Format a ZAR integer amount in the user's current display currency. */
export function fmtPrice(zarAmount, currency, fxRate = DEFAULT_FX_RATE) {
    if (zarAmount === 0) return currency === "USD" ? "$0" : "R0";
    if (currency === "USD") {
        const usd = Math.round(zarAmount / fxRate);
        return `$${usd.toLocaleString("en-US")}`;
    }
    return `R${zarAmount.toLocaleString("en-ZA").replace(/,/g, " ")}`;
}
